import { ATLAS_AI_NOTE_WORKING, UPDATE_ANALYSIS_EXPORT } from '@common/constants/webSockets'
import { buildLayoutAssignments } from '@common/helpers/commentaryLayout'
import {
  ICommentary,
  ICommentaryLayoutRow,
  IDefaultCommentarySection,
} from '@common/interfaces/commentary'
import {
  IAmendmentBoardMemberRow,
  IAmendmentRow,
  IGeneralAnalysis,
  IGeneralAnalysisEvent,
  IGeneralAnalysisExhibit,
  IGeneralAnalysisSection,
} from '@common/interfaces/generalAnalysis'
import { INote } from '@common/interfaces/notes'
import { combineReducers } from 'redux'
import { fillImageRenderData } from '../../helpers/editorImageData'
import { loadingArrayReducer } from '../../helpers/helpers'
import { createLoadingDataReducer } from '../../helpers/redux'
import { IAction } from '../apiMiddleware'
import { ADD_NOTE_SUCCESS, DELETE_NOTE_SUCCESS, UPDATE_NOTE_SUCCESS } from '../notes/actions'
import { insertReply, removeFromTree, updateInTree } from '../notes/threadHelpers'
import * as actions from './actions'

interface IListData {
  data: IAmendmentRow[]
  totalItems: number
}

interface IEventListData {
  data: IGeneralAnalysisEvent[]
}

interface IBoardMemberListData {
  data: IAmendmentBoardMemberRow[]
}

export interface ICommentaryData {
  sections: IDefaultCommentarySection[]
  commentaries: ICommentary[]
}

// Notes endpoints joined-load `parent` so we can identify which
// commentary thread a reply belongs to. Only the fields read here are
// typed; the rest of the note payload is the standard INote.
type INoteWithParent = INote & { parent?: { commentaryId?: string } }

// A save echo hydrates only the images that save added, so the ones already
// loaded keep the bytes the store holds for them.
const withPreviousImageData = (updated: ICommentary, previous: ICommentary): ICommentary =>
  updated.note?.note && previous.note?.note
    ? {
        ...updated,
        note: { ...updated.note, note: fillImageRenderData(updated.note.note, previous.note.note) },
      }
    : updated

export default combineReducers({
  // Auto-tracks every `{prefix}/*_REQUEST` and removes on
  // `{prefix}/*_(SUCCESS|FAILURE)`. Stays in sync as new actions are
  // added — replaces the hand-maintained switch we had before.
  loadingArray: loadingArrayReducer([actions.prefix]),
  list: createLoadingDataReducer<IListData>([actions.LIST_REQUEST]),
  boardMembers: createLoadingDataReducer<IBoardMemberListData>([
    actions.LIST_BOARD_MEMBERS_REQUEST,
  ]),
  current(state: IGeneralAnalysis | null = null, action: IAction): IGeneralAnalysis | null {
    switch (action.type) {
      case actions.SHOW_SUCCESS:
        return action.data as IGeneralAnalysis
      case actions.UPDATE_STATUS_SUCCESS:
      case actions.UPDATE_ASSIGNEE_SUCCESS:
      case actions.UPLOAD_SIGNED_DOCUMENTS_SUCCESS: {
        if (!state) return state
        const patch = action.data as Partial<IGeneralAnalysis>
        // Deep-merge `amendmentDetails` instead of replacing it. The
        // UpdateStatus/UpdateAssignee responses load it WITHOUT the nested
        // subject relations (`termLoan` / `overadvance` / `clientAmendment`
        // + its details) that the initial Show loaded — a plain spread
        // would wipe them from `current`, and the execution-confirmation
        // modal (which reads the subject entity to render the terms
        // table) would silently lose its data after any in-session status
        // transition.
        const mergedDetails = patch.amendmentDetails
          ? { ...state.amendmentDetails, ...patch.amendmentDetails }
          : state.amendmentDetails
        return { ...state, ...patch, amendmentDetails: mergedDetails }
      }
      case actions.REGENERATE_EXPORT_SUCCESS:
        return state ? { ...state, lastExportedAt: new Date().toISOString() } : state
      // A background export (approval, execution, effective-date refresh)
      // finished server-side; take its timestamp so the stale badge clears.
      case UPDATE_ANALYSIS_EXPORT: {
        const { analysisId, lastExportedAt } = action.data as {
          analysisId: string
          lastExportedAt: string
        }
        if (!state || state.id !== analysisId) return state
        return { ...state, lastExportedAt }
      }
      case actions.SAVE_VIEW_SUCCESS: {
        if (!state) return state
        // The SaveView response returns only the saved-view slice (+ bumped
        // contentUpdatedAt); merge it onto `current` without touching the
        // rest of the loaded analysis.
        const patch = action.data as Partial<IGeneralAnalysis>
        return {
          ...state,
          savedView: patch.savedView ?? null,
          savedViewUpdatedAt: patch.savedViewUpdatedAt ?? null,
          savedViewUpdatedById: patch.savedViewUpdatedById ?? null,
          savedViewUpdatedBy: patch.savedViewUpdatedBy ?? null,
          contentUpdatedAt: patch.contentUpdatedAt ?? state.contentUpdatedAt,
        }
      }
      // A layout save bumps contentUpdatedAt server-side (the arrangement is
      // part of the exported writeup). Merge the fresh timestamp so the
      // Export "stale" badge agrees with the server's Executed gate without
      // a reload.
      case actions.SAVE_COMMENTARY_LAYOUT_SUCCESS: {
        if (!state) return state
        const patch = action.data as { analysisId?: string; contentUpdatedAt?: string }
        if (!patch?.contentUpdatedAt || patch.analysisId !== state.id) return state
        return { ...state, contentUpdatedAt: patch.contentUpdatedAt }
      }
      case actions.UPDATE_EXHIBIT_SUCCESS: {
        if (!state) return state
        const updated = action.data as IGeneralAnalysisExhibit | undefined
        if (!updated?.exhibitKey) return state
        const existing: IGeneralAnalysisExhibit[] = state.exhibits ?? []
        const idx = existing.findIndex((e) => e.exhibitKey === updated.exhibitKey)
        const next: IGeneralAnalysisExhibit[] =
          idx === -1
            ? [...existing, updated]
            : existing.map((e, i) => (i === idx ? { ...e, ...updated } : e))
        return { ...state, exhibits: next }
      }
      case actions.UPDATE_SECTION_SUCCESS: {
        if (!state) return state
        const updated = action.data as
          (IGeneralAnalysisSection & { contentUpdatedAt?: string | null }) | undefined
        if (!updated?.id || updated.analysisId !== state.id) return state
        const { id, analysisId, sectionKey, isNotApplicable, createdAt, updatedAt } = updated
        const section: IGeneralAnalysisSection = {
          id,
          analysisId,
          sectionKey,
          isNotApplicable,
          createdAt,
          updatedAt,
        }
        const existing: IGeneralAnalysisSection[] = state.sections ?? []
        const sections = existing.some((row) => row.id === id)
          ? existing.map((row) => (row.id === id ? { ...row, ...section } : row))
          : [...existing, section]
        return {
          ...state,
          sections,
          contentUpdatedAt: updated.contentUpdatedAt ?? state.contentUpdatedAt,
        }
      }
      default:
        return state
    }
  },
  events(state: IEventListData | null = null, action: IAction): IEventListData | null {
    switch (action.type) {
      case actions.LIST_EVENTS_SUCCESS:
        return action.data as IEventListData
      default:
        return state
    }
  },
  commentary(state: ICommentaryData | null = null, action: IAction): ICommentaryData | null {
    switch (action.type) {
      case actions.LIST_COMMENTARY_SUCCESS:
        return action.data as ICommentaryData
      case actions.ADD_COMMENTARY_SUCCESS: {
        const added = action.data as ICommentary
        return state
          ? { ...state, commentaries: [...state.commentaries, added] }
          : { sections: [], commentaries: [added] }
      }
      case actions.UPDATE_COMMENTARY_SUCCESS: {
        const updated = action.data as ICommentary
        return state
          ? {
              ...state,
              commentaries: state.commentaries.map((c) =>
                c.id === updated.id ? withPreviousImageData(updated, c) : c,
              ),
            }
          : state
      }
      case actions.DELETE_COMMENTARY_SUCCESS: {
        const deleted = action.data as { id: string }
        return state
          ? {
              ...state,
              commentaries: state.commentaries.filter((c) => c.id !== deleted.id),
            }
          : state
      }
      // Optimistic: applied on REQUEST so the box lands where it was
      // dropped without waiting for the round-trip. Uses the same
      // buildLayoutAssignments walk the server persists — on failure the
      // caller refetches (listCommentary with skipLoader) to resync.
      case actions.SAVE_COMMENTARY_LAYOUT_REQUEST: {
        if (!state) return state
        const { rows } = action.params as { rows: ICommentaryLayoutRow[] }
        const assignments = buildLayoutAssignments(rows)
        return {
          ...state,
          commentaries: state.commentaries.map((c) => {
            const assignment = assignments.get(c.id)
            if (!assignment) return c
            return {
              ...c,
              layoutRow: assignment.layoutRow,
              layoutExhibit: assignment.layoutExhibit,
              note: c.note ? { ...c.note, dragOrder: assignment.dragOrder } : c.note,
            }
          }),
        }
      }
      // The exhibit thread anchor has to be in this list before a reply can
      // find it (see ADD_NOTE_SUCCESS below). Idempotent: the service returns
      // the existing anchor when there is one.
      case actions.ENSURE_EXHIBIT_THREAD_SUCCESS: {
        if (!state) return state
        const anchor = action.data as ICommentary | undefined
        if (!anchor?.id || state.commentaries.some((c) => c.id === anchor.id)) return state
        return { ...state, commentaries: [...state.commentaries, anchor] }
      }
      // Replies on a commentary go through the shared `notes` redux module
      // (same path LCR uses), so we mirror dueDiligence's reducer here.
      //
      // The parent of a reply is no longer necessarily the commentary's anchor
      // note — it can be another reply, at any depth. So instead of matching an
      // id against the anchor, each case asks the tree helper to do the work and
      // detects a hit by reference: the helpers return the ORIGINAL array when
      // nothing matched, so an untouched commentary re-renders nothing.
      case ADD_NOTE_SUCCESS: {
        if (!state) return state
        const reply = action.data as INoteWithParent | undefined
        if (!reply) return state
        const parentCommentaryId = reply.parent?.commentaryId
        if (!reply.parentId && !parentCommentaryId) return state
        return {
          ...state,
          commentaries: state.commentaries.map((c) => {
            if (!c.note) return c
            // No parentId on the payload — fall back to the joined
            // `parent.commentaryId`, which can only ever mean the anchor note.
            if (!reply.parentId) {
              return c.id === parentCommentaryId
                ? { ...c, note: { ...c.note, children: [...(c.note.children ?? []), reply] } }
                : c
            }
            const [note] = insertReply([c.note], reply)
            return note === c.note ? c : { ...c, note }
          }),
        }
      }
      case UPDATE_NOTE_SUCCESS: {
        if (!state) return state
        const updated = action.data as INoteWithParent | undefined
        if (!updated?.id) return state
        return {
          ...state,
          commentaries: state.commentaries.map((c) => {
            if (!c.note?.children?.length) return c
            // Scoped to the replies: the anchor note's own body is updated
            // through the commentary services, not this action.
            const children = updateInTree(c.note.children, updated)
            return children === c.note.children ? c : { ...c, note: { ...c.note, children } }
          }),
        }
      }
      case DELETE_NOTE_SUCCESS: {
        const deleted = action.data as { id?: string } | undefined
        if (!state || !deleted?.id) return state
        const deletedId = deleted.id
        return {
          ...state,
          commentaries: state.commentaries.map((c) => {
            if (!c.note?.children?.length) return c
            const children = removeFromTree(c.note.children, deletedId)
            return children === c.note.children ? c : { ...c, note: { ...c.note, children } }
          }),
        }
      }
      // Arc flipped its working state on a commentary thread (pushed live over
      // the websocket — the same event the notes slice listens to). Flip the
      // matching commentary's anchor-note `isArcWorking` so the "Arc is working…"
      // indicator appears/disappears without a refetch. Keyed by root note id.
      case ATLAS_AI_NOTE_WORKING: {
        if (!state) return state
        const { noteId, isArcWorking } = action.data as {
          noteId: string
          isArcWorking: boolean
        }
        return {
          ...state,
          commentaries: state.commentaries.map((c) =>
            c.note?.id === noteId ? { ...c, note: { ...c.note, isArcWorking } } : c,
          ),
        }
      }
      default:
        return state
    }
  },
})
